import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {BULK_USER_IMPORT_FILE_NAME} from 'constants/UserConstants';
import {WithoutTypenames} from 'graphql-client/WithoutTypenames';
import notifications from 'notifications';
import {OrganizationPermissionRole} from 'orgs/OrganizationConstants';
import {LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import * as TenantActions from 'tenants/TenantActions';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import AddIcon from 'ui-library/icons/AddIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import Modal from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';
import {textToBlob} from 'util/TextToBlobUtil';
import {InputChangeEvent, TextAreaChangeEvent} from 'util/Types';

import {CreateUsersModalFragment} from './__generated/CreateUsersModalFragment';
import {GroupRowInfo} from './CreateUsersGroupSection';
import OrganizationsSection, {EMPTY_ORGANIZATION_INFO} from './CreateUsersOrganizationsSection';
import TeamsSection, {EMPTY_TEAM_INFO} from './CreateUsersTeamsSection';
import UsersForm, {EMPTY_USER_ROW_INFO, UserRowInfo} from './UsersForm';

type GeneratedCreateUsersModalTeam = CreateUsersModalFragment['currentTenantTeams'][number];
type GeneratedCreateUsersModalOrganization = CreateUsersModalFragment['currentTenantOrganizations'][number];

export type CreateUsersModalTeam = WithoutTypenames<GeneratedCreateUsersModalTeam>;
export type CreateUsersModalOrganization = WithoutTypenames<GeneratedCreateUsersModalOrganization>;

export const CREATE_USERS_MODAL_FRAGMENT: DocumentNode = gql`
  fragment CreateUsersModalFragment on Query {
    currentTenantOrganizations {
      id
      name
    }
    currentTenantTeams {
      id
      name
      organization {
        id
      }
    }
  }
`;

// Keep in sync with create_users.py
const MAX_NUM_USERS_TO_CREATE = 100;

interface Props {
  onRequestClose: () => {};
  initialOrganizationId?: string | null;
  canSelectOrganizations?: boolean;
  organizations: Array<CreateUsersModalOrganization>;
  teams: Array<CreateUsersModalTeam>;
  showBenchlingAdminControls?: boolean;
  onSuccess: () => void;
}

interface State {
  errorMessage: string | null;
  rawText: string;
  isCreating: boolean;
  teamRowInfos: Array<GroupRowInfo<CreateUsersModalTeam>>;
  organizationRowInfos: Array<GroupRowInfo<CreateUsersModalOrganization>>;
  shouldSendEmails: boolean;
  userRowInfos: Array<UserRowInfo>;
  showBulkView: boolean;
}

function getInitialOrganizationRowInfo(
  organizations: Array<CreateUsersModalOrganization>,
  initialOrganizationId?: string | null
): GroupRowInfo<CreateUsersModalOrganization> {
  const initialOrganization = organizations.find((organization) => organization.id === initialOrganizationId);
  return initialOrganization
    ? {group: initialOrganization, role: OrganizationPermissionRole.MEMBER}
    : EMPTY_ORGANIZATION_INFO;
}

// Exported for testing
export function validateUserCreationSpreadsheetText(rawText: string): boolean {
  const rowLength = rawText.split(/[\n\r\t]+/g).length;
  const segments = rawText.replace(/[\n\r\t]+/g, ',').split(',');
  return (
    rowLength <= MAX_NUM_USERS_TO_CREATE &&
    segments.length % 3 === 0 &&
    segments.every((segment) => Boolean(segment))
  );
}

export default class CreateUsersModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      rawText: '',
      errorMessage: null,
      isCreating: false,
      userRowInfos: [EMPTY_USER_ROW_INFO],
      organizationRowInfos: [getInitialOrganizationRowInfo(props.organizations, props.initialOrganizationId)],
      teamRowInfos: [EMPTY_TEAM_INFO],
      shouldSendEmails: true,
      showBulkView: false,
    };
  }

  static defaultProps = {
    canSelectOrganizations: true,
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSubmit = async () => {
    let usersSpreadsheetURL = null;
    let userInfos = null;
    if (this.state.showBulkView) {
      const blob = textToBlob(this.state.rawText);
      const fileName = BULK_USER_IMPORT_FILE_NAME;
      const file = new File([blob], fileName, {
        type: blob.type,
      });
      usersSpreadsheetURL = await ImportUtil.upload(file);
    } else {
      userInfos = this.state.userRowInfos;
    }
    const organizationInfos = this.state.organizationRowInfos
      .filter((organizationRowInfo) => organizationRowInfo.group != null)
      .map((organizationRowInfo) => ({
        organizationId: organizationRowInfo.group!.id,
        role: organizationRowInfo.role,
      }));
    const teamInfos = this.state.teamRowInfos
      .filter((teamRowInfo) => teamRowInfo.group != null)
      .map((teamRowInfo) => ({
        teamId: teamRowInfo.group!.id,
        role: teamRowInfo.role,
      }));
    this.setState({isCreating: true});
    try {
      await TenantActions.createUsers(
        usersSpreadsheetURL,
        userInfos,
        organizationInfos,
        teamInfos,
        this.state.shouldSendEmails
      );
      this.props.onSuccess();
      this.props.onRequestClose();
    } catch (err) {
      if (net.isServerRequestError(err)) {
        this.setState({
          errorMessage: notifications.getFriendlyError(err),
        });
      } else {
        throw err;
      }
    } finally {
      this.setState({isCreating: false});
    }
  };

  _handleChangeRawText = (e: TextAreaChangeEvent): void => {
    this.setState({rawText: e.target.value});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleToggleView = () => {
    this.setState({showBulkView: !this.state.showBulkView});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleAddUser = () => {
    this.setState({userRowInfos: [...this.state.userRowInfos, EMPTY_USER_ROW_INFO]});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleUpdateUserRowInfos = (userRowInfos: Array<UserRowInfo>) => {
    if (userRowInfos.length === 0) {
      this.setState({userRowInfos: [EMPTY_USER_ROW_INFO]});
      return;
    }
    this.setState({userRowInfos});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleUpdateTeamRowInfos = (teamRowInfos: Array<GroupRowInfo<CreateUsersModalTeam>>) => {
    this.setState({teamRowInfos});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleUpdateOrganizationRowInfos = (
    organizationRowInfos: Array<GroupRowInfo<CreateUsersModalOrganization>>
  ) => {
    const organizationIds = organizationRowInfos
      .filter((organizationRowInfo) => organizationRowInfo.group != null)
      .map((organizationRowInfo) => organizationRowInfo.group!.id);
    // Remove any teams that are not part of current organizations.
    const teamRowInfos = this.state.teamRowInfos.filter(
      (teamRowInfo) =>
        teamRowInfo.group == null || organizationIds.includes(teamRowInfo.group.organization.id)
    );
    // If we removed all team rows, add an empty row back.
    if (teamRowInfos.length === 0) {
      teamRowInfos.push(EMPTY_TEAM_INFO);
    }

    this.setState({
      organizationRowInfos,
      teamRowInfos,
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleUpdateSendEmails = (e: InputChangeEvent) => {
    this.setState({shouldSendEmails: e.target.checked});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _isValid = () =>
    this.state.showBulkView
      ? this._hasValidSpreadsheetInfo()
      : !this._hasOnlyInitialUserRowInfo() && !this._hasInvalidEmail();

  _hasOnlyInitialUserRowInfo = (): boolean =>
    this.state.userRowInfos.length === 1 && this.state.userRowInfos[0] === EMPTY_USER_ROW_INFO;

  _hasInvalidEmail = (): boolean => _.any(this.state.userRowInfos.map((info) => info.hasEmailError));

  _hasValidSpreadsheetInfo = (): boolean => validateUserCreationSpreadsheetText(this.state.rawText);

  render(): JSX.Element {
    const selectedOrganizationIds = this.state.organizationRowInfos
      .filter((organizationRowInfo) => organizationRowInfo.group != null)
      .map((organizationRowInfo) => organizationRowInfo.group!.id);
    const selectableTeams = this.props.teams.filter((team) =>
      selectedOrganizationIds.includes(team.organization.id)
    );
    return (
      <div>
        <Modal
          onRequestClose={this.props.onRequestClose}
          onRequestSubmit={this._handleSubmit}
          title="Create members"
          buttons={[
            {
              text: this.state.isCreating ? 'Creating...' : 'Create',
              submit: true,
              disabled: this.state.isCreating || !this._isValid(),
              style: ButtonStyle.SECONDARY,
            },
          ]}
        >
          <div className="u-lr-split u-small-spacing-bottom">
            <div>
              <FieldLabel label="Users" />
              {!this.state.showBulkView && (
                <Tooltip title="Use spreadsheet upload to create more than 10 users.">
                  <AddIcon
                    className="u-mini-spacing-left"
                    onClick={this._handleAddUser}
                    disabled={this.state.userRowInfos.length >= 10}
                  />
                </Tooltip>
              )}
            </div>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              text={this.state.showBulkView ? 'Form view' : 'Upload spreadsheet'}
              onClick={this._handleToggleView}
              size={ButtonSize.SMALL}
            />
          </div>
          {this.state.showBulkView ? (
            <InputWrapper
              id="primary-sample-input"
              label="Users"
              labelStyle={FieldLabel.STYLES.HIDDEN}
              description={`Name, Email, Username (${MAX_NUM_USERS_TO_CREATE} max)`}
              descriptionClassName="u-faded"
            >
              <textarea
                autoFocus={true}
                onChange={this._handleChangeRawText}
                value={this.state.rawText}
                rows={8}
              />
            </InputWrapper>
          ) : (
            <UsersForm userRowInfos={this.state.userRowInfos} onUpdate={this._handleUpdateUserRowInfos} />
          )}
          {BenchlingConfig.TENANT_HAS_SAML && (
            <div>
              <strong>Note:</strong> For SAML SSO, user emails and usernames must match your SAML identity
              provider to ensure they can log in to the correct account.
            </div>
          )}
          {this.state.errorMessage && (
            <div className={css(styles.errorMessage)}>{this.state.errorMessage}</div>
          )}
          {this.props.organizations && this.props.organizations.length > 0 && (
            <OrganizationsSection
              disabled={!this.props.canSelectOrganizations}
              organizations={this.props.organizations}
              organizationRowInfos={this.state.organizationRowInfos}
              onUpdate={this._handleUpdateOrganizationRowInfos}
            />
          )}
          {this.props.teams && this.props.teams.length > 0 && (
            <TeamsSection
              teams={selectableTeams}
              teamRowInfos={this.state.teamRowInfos}
              onUpdate={this._handleUpdateTeamRowInfos}
            />
          )}
          <div className="u-spacing-top">
            <CheckboxInput
              checked={this.state.shouldSendEmails}
              onChange={this._handleUpdateSendEmails}
              id="send-email"
              label="Send an email to new users with instructions on how to activate their account"
              dataTestAttrs={{component: 'CreateUsersModal', element: 'checkbox'}}
            />
          </div>
        </Modal>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  errorMessage: {
    color: Colors.RED_LIGHT,
    marginTop: 6,
    whiteSpace: 'pre-wrap',
  },
  linkText: {
    ...LEGACY_FONT_PROXIMA_13,
    textDecoration: 'none',
  },
});
